"use client";

import {
  AccountOverview,
  AccountOverviewRequestArgs,
  AccountOverviewWsMessage,
} from "@decibeltrade/sdk";
import { useEffect } from "react";

import { signedTradeFee } from "@/utils/trade-action";

import { readDexSdk } from "../dex-sdk";
import { accountOverviewQueryKey, type CachedAccountOverview } from "../queries/account-overview";
import { useRegisterSubscription } from "../subscription-provider";

export function realizedPnlNetOfFees(
  trades: ReadonlyArray<{ realized_pnl_amount: number; fee_amount: number; is_rebate: boolean }>,
): number {
  return trades.reduce((acc, trade) => acc + trade.realized_pnl_amount - signedTradeFee(trade), 0);
}

/**
 * Subscribes to account overview via websocket and updates the query cache as messages are received.
 */
export function useAccountOverviewSubscription(args: AccountOverviewRequestArgs) {
  const registerSubscription = useRegisterSubscription();

  const { subAddr, volumeWindow, includePerformance } = args;

  useEffect(() => {
    if (!subAddr) {
      return;
    }

    const queryKey = accountOverviewQueryKey({
      subAddr,
      volumeWindow,
      includePerformance,
    });

    const unregister = registerSubscription(queryKey, (queryClient) => {
      let unsubscribeUserTradeHistory: (() => void) | undefined = undefined;

      // Update realized PNL in account overview when user trade history is received
      // Account overview does not include realized PNL, so we need to subscribe to user trade history to update it
      if (includePerformance) {
        unsubscribeUserTradeHistory = readDexSdk.userTradeHistory.subscribeByAddr(
          subAddr,
          (data) => {
            const totalRealizedPnl = realizedPnlNetOfFees(data.trades);

            queryClient.setQueryData<AccountOverview>(queryKey, (prev) => {
              if (!prev) return prev;

              const newData = {
                ...prev,
                realized_pnl: (prev.realized_pnl ?? 0) + totalRealizedPnl,
                pnl_90d: (prev.pnl_90d ?? 0) + totalRealizedPnl,
              };

              return newData;
            });
          },
        );
      }

      const unsubscribeAccountOverview = readDexSdk.accountOverview.subscribeByAddr(
        subAddr,
        (data) => {
          queryClient.setQueryData<AccountOverview>(queryKey, (prev) =>
            mergeAccountOverviewWsUpdate(prev, data.account_overview),
          );
        },
      );

      return () => {
        unsubscribeUserTradeHistory?.();
        unsubscribeAccountOverview();
      };
    });

    return unregister;
  }, [subAddr, volumeWindow, registerSubscription, includePerformance]);
}

/**
 * Merge a WS account_overview message into the cached value. Extracted from
 * the subscription so the field whitelist is unit-testable.
 */
export function mergeAccountOverviewWsUpdate(
  prev: CachedAccountOverview | undefined,
  incoming: AccountOverviewWsMessage["account_overview"],
): CachedAccountOverview {
  if (!prev) {
    return { ...incoming, volume: null };
  }

  const { isPlaceholder: _placeholder, ...authoritative } = prev;

  // Only these fields on account_overview are updated in the ws response
  const {
    cross_account_leverage_ratio,
    cross_account_position,
    cross_available_to_trade,
    cross_margin_ratio,
    free_vault_equity,
    maintenance_margin,
    perp_equity_balance,
    perp_equity_haircutted,
    secondary_collateral,
    spot,
    total_margin,
    unrealized_funding_cost,
    unrealized_pnl,
    usdc_cross_balance,
    usdc_cross_withdrawable_balance,
    usdc_isolated_withdrawable_balance,
    vault_equity,
  } = incoming;

  return {
    ...authoritative,
    cross_account_leverage_ratio,
    cross_account_position,
    cross_margin_ratio,
    maintenance_margin,
    perp_equity_balance,
    total_margin,
    unrealized_funding_cost,
    unrealized_pnl,
    usdc_cross_withdrawable_balance,
    usdc_isolated_withdrawable_balance,
    vault_equity,
    // Only overwrite when the WS server actually sends these fields.
    // Use != null (loose) to guard against both null and undefined — the WS server
    // sends null when the account has no secondary collateral in its snapshot state,
    // which would wipe the HTTP-fetched value. Keep HTTP value in that case.
    ...(cross_available_to_trade != null && { cross_available_to_trade }),
    ...(usdc_cross_balance != null && { usdc_cross_balance }),
    ...(secondary_collateral != null && { secondary_collateral }),
    ...(free_vault_equity != null && { free_vault_equity }),
    ...(perp_equity_haircutted != null && { perp_equity_haircutted }),
    // Same guard: enrich_with_spot always sends Some (an emptied wallet is an
    // empty positions array, which must overwrite); null only means the server
    // didn't enrich (old version / failure) and shouldn't wipe the HTTP value.
    ...(spot != null && { spot }),
  };
}
